/**
 * One automation, laid out like Cursor's automation editor: a top bar (the
 * breadcrumb, Save, Ask the Builder, Test, Run on a Case and a menu), the
 * title, a meta row (the Enable switch, its visas, who wrote it), then
 * Overview, Runs and History (`?view=`). Overview is the automation in
 * place, top to bottom: the
 * instructions (with "Optimize With Builder"), and, folded under Advanced,
 * what sets it off, what it may do, the automations it follows and its
 * references. Whoever the firm lets write
 * skills types the title and instructions, and enables, tests, duplicates,
 * archives or deletes it here; anyone may run it on a case. CaseBlink writes
 * the description agents match on (Edit Description in the menu changes
 * it); the raw SKILL.md opens from the menu too. Leaving with unsaved
 * changes asks first.
 *
 * `isNew` is the same page for a skill that isn't saved yet (New Automation, a
 * starter or a duplicate): no runs or history, nothing to run on a case, and
 * the first Save creates it, enabled unless the switch is off. Test saves it
 * first. The right-hand side of the builder view.
 */
import {
  Archive,
  ArrowLeft,
  ChevronRight,
  Copy,
  Eye,
  EyeOff,
  FileText,
  FlaskConical,
  MoreHorizontal,
  Pencil,
  Play,
  Sparkles,
  TextQuote,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  Wand2
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";
import { useLocation, useNavigate, type Location } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import { ListCard } from "@/components/ui/list-card";
import { MetaLine } from "@/components/ui/meta-line";
import { SectionHeader } from "@/components/ui/page-header";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge } from "@/components/ui/status-badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { firmKnowledgeApi } from "@/api/firm-knowledge";
import { ChatProse } from "@/features/agents/refs/ChatProse";
import { caseWorkspacePath } from "@/features/caseIde/routes";
import {
  RUN_PARAM,
  THREAD_PARAM
} from "@/features/caseIde/chat/useThreadDeepLink";
import { invalidateActiveFirmSkills } from "@/features/firmKnowledge/hooks/useActiveFirmSkills";
import type { FirmKnowledgeScope } from "@/features/firmKnowledge/hooks/useFirmScope";
import type { FirmSkillsState } from "@/features/firmKnowledge/hooks/useFirmSkills";
import { useSkillRuns } from "@/features/firmKnowledge/hooks/useSkillRuns";
import {
  automationsPath,
  skillViewOf,
  type SkillView
} from "@/features/automations/routes";
import { firmKnowledgePath } from "@/features/firmKnowledge/routes";
import { VisaPicker } from "@/features/firmKnowledge/components/VisaPicker";
import type {
  FirmLibraryEntry,
  FirmSkill,
  FirmSkillStatus,
  SkillEdits
} from "@/features/firmKnowledge/types";
import { skillVisas } from "./caseSuggestions";
import { DeleteSkillDialog } from "./DeleteSkillDialog";
import { EditDescriptionDialog } from "./EditDescriptionDialog";
import { InstructionsEditor } from "./InstructionsEditor";
import { RunSkillDialog, type RunSkillRequest } from "./RunSkillDialog";
import { SkillEditor } from "./SkillEditor";
import { SkillSaveBar } from "./SkillSaveBar";
import { SkillHistory } from "./SkillHistory";
import { SkillRunsList } from "./SkillRunsList";
import {
  AuthoritySection,
  SkillSettingsSave,
  TriggersSection,
  useSkillSettingsDraft,
  type SkillContent,
  type SkillSettingsDraft
} from "./SkillSettings";
import { fullDate, shortDate, SKILL_STATUS_LABEL } from "./skillModel";
import { SuggestedReferences } from "./SuggestedReferences";
import { UnsavedChangesGuard } from "./UnsavedChangesGuard";
import { UpcomingAutomations } from "./UpcomingAutomations";

const Section: FC<{
  title: string;
  testId?: string;
  children: ReactNode;
}> = ({ title, testId, children }) => (
  <section className="flex flex-col gap-3" data-testid={testId}>
    <SectionHeader title={title} />
    {children}
  </section>
);

const Separator: FC = () => (
  <span className="h-3.5 w-px shrink-0 bg-line-2" aria-hidden />
);

type Tab = SkillView | "markdown";

const MODE_LABEL = { agent: "Do the Work", plan: "Plan First" } as const;

/**
 * The instructions footer's mode line: "Do the Work", or "Runs You Start:
 * Do the Work · Automations: Plan First" when the triggers start runs
 * another way.
 */
export const modeLine = (
  draft: Pick<SkillSettingsDraft, "authority" | "triggers">
): string => {
  const own = MODE_LABEL[draft.authority.default_mode] ?? MODE_LABEL.agent;
  const modes = new Set(
    draft.triggers
      .filter((t) => t.enabled !== false)
      .map((t) => t.mode ?? "plan")
  );
  if (!modes.size) return own;
  const [only] = [...modes];
  if (modes.size === 1 && only === draft.authority.default_mode) return own;
  const theirs = modes.size === 1 ? MODE_LABEL[only] : "Each Its Own";
  return `Runs You Start: ${own} · Automations: ${theirs}`;
};

/** Which part of the URL names the page; a tab switch (`view`) isn't leaving. */
const pageOf = (loc: Location): string => {
  const p = new URLSearchParams(loc.search);
  return [
    loc.pathname,
    p.get("tab"),
    p.get("skill"),
    p.get("new"),
    p.get("starter"),
    p.get("duplicate")
  ].join("|");
};

const isSaveKey = (e: KeyboardEvent) =>
  (e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === "s";

/** Navigation state that opens the skill's test dialog (Test on a new skill). */
export const SKILL_TEST_STATE = "skillTest";

const OPTIMIZE_KEY = "automations:optimize-with-builder";

/** Whether Save asks the builder to tighten the instructions; remembered per browser. */
const readOptimizePref = (): boolean => {
  try {
    return window.localStorage.getItem(OPTIMIZE_KEY) === "1";
  } catch {
    return false;
  }
};

const writeOptimizePref = (on: boolean) => {
  try {
    window.localStorage.setItem(OPTIMIZE_KEY, on ? "1" : "0");
  } catch {
    // The switch still works for this visit.
  }
};

/** The soft lavender accent look (as Let CaseBlink Prepare), on a Button. */
const CASEY_BUILD =
  "bg-accent-soft text-accent-seed inset-ring inset-ring-accent-line hover:bg-accent-line/50 hover:text-accent-seed";

type Props = {
  skill: FirmSkill;
  skills: FirmSkillsState;
  firm: FirmKnowledgeScope;
  /** Active library entries, for the reference picker and `@` mentions. */
  library: FirmLibraryEntry[];
  /** Start editing straight away (the "Edit markdown" advanced path). */
  initialEditing?: boolean;
  /** The top bar's left side: the breadcrumb (and a back arrow). */
  leading?: ReactNode;
  /**
   * Open the builder beside the page, with what's typed so far. Shows "Ask
   * the Builder" in the top bar and "Let Casey Build It for You" with the
   * instructions (over the box while it is empty, in its footer otherwise).
   */
  onAskBuilder?: (content: SkillContent) => void;
  /** Not saved yet: the first Save creates it. */
  isNew?: boolean;
  /** Where a new skill's unsaved draft waits: `starter:<id>`, `duplicate:<id>` or `blank`. */
  draftKey?: string;
  /** A new skill was just created by Save; `test`: by Test, so open its test. */
  onCreated?: (
    skill: FirmSkill,
    opts?: { test?: boolean; optimize?: boolean }
  ) => void;
  /** Leave a new skill without saving it. */
  onDiscard?: () => void;
};

export const SkillPreview: FC<Props> = ({
  skill,
  skills,
  firm,
  library,
  initialEditing = false,
  leading,
  onAskBuilder,
  isNew = false,
  draftKey,
  onCreated,
  onDiscard
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [editing, setEditing] = useState(initialEditing);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState<"run" | "test" | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [describing, setDescribing] = useState(false);
  const [needsInstructions, setNeedsInstructions] = useState(false);
  const [optimize, setOptimize] = useState(readOptimizePref);
  const [advancedOpen, setAdvancedOpen] = useState<boolean | null>(null);
  // Set when a failed check has to open Overview first; the card mounts a
  // beat later (its tab content wasn't on the page), and focuses then.
  const pendingInstructionFocus = useRef(false);
  const instructionsRef = useRef<HTMLDivElement | null>(null);
  const instructionsCardRef = useCallback((node: HTMLDivElement | null) => {
    instructionsRef.current = node;
    if (!node || !pendingInstructionFocus.current) return;
    pendingInstructionFocus.current = false;
    node.querySelector<HTMLTextAreaElement>("textarea")?.focus();
  }, []);
  const [markdownOpen, setMarkdownOpen] = useState(initialEditing);
  const view: SkillView = isNew ? "overview" : skillViewOf(location.search);
  const tab: Tab = markdownOpen ? "markdown" : view;
  const runs = useSkillRuns(isNew ? null : skill.id, firm.scope);
  const draft = useSkillSettingsDraft(skill, skills, firm.scope, {
    isNew,
    draftKey
  });
  const { content, setContent } = draft;
  const { canEdit } = firm;
  const { metrics } = runs;
  const { automated } = metrics;
  // The switch answers at once; the server's word replaces it (or rolls it back).
  const [pendingStatus, setPendingStatus] = useState<FirmSkillStatus | null>(
    null
  );
  const status: FirmSkillStatus = isNew
    ? content.status
    : pendingStatus ?? skill.status;
  const active = status === "active";
  const ready = Boolean(content.instructions.trim());
  const instructionsError = needsInstructions && !ready;
  const visas = skillVisas(skill);
  const updated = shortDate(skill.updated_at ?? skill.created_at);
  const author = skill.created_by_name ?? skill.updated_by_name;
  const firstReference = skill.references.find((r) => !r.missing);
  const dirty = canEdit && draft.changed.length > 0;

  // Moves this page makes itself (after a create, a discard or a delete)
  // aren't "leaving with unsaved changes".
  const allowLeave = useRef(false);
  const leaves = useCallback(
    (from: Location, to: Location) =>
      !allowLeave.current && pageOf(from) !== pageOf(to),
    []
  );

  // A blank new skill starts in its title, once the title is editable
  // (permissions can arrive after the page).
  const titleRef = useRef<HTMLInputElement | null>(null);
  const focusedTitle = useRef(false);
  useEffect(() => {
    if (focusedTitle.current || !canEdit) return;
    focusedTitle.current = true;
    if (isNew && !content.title) titleRef.current?.focus();
  }, [canEdit, isNew, content.title]);

  // Test on a new skill saves it, then opens its test on the saved page.
  const testOnOpen = Boolean(
    (location.state as Record<string, unknown> | null)?.[SKILL_TEST_STATE]
  );
  useEffect(() => {
    if (!testOnOpen || isNew) return;
    setRunning("test");
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: null }
    );
  }, [testOnOpen, isNew, navigate, location.pathname, location.search]);

  const setTab = (next: Tab) => {
    if (next === "markdown") {
      setMarkdownOpen(true);
      return;
    }
    setMarkdownOpen(false);
    setEditing(false);
    if (next === view) return;
    const params = new URLSearchParams(location.search);
    if (next === "overview") params.delete("view");
    else params.set("view", next);
    navigate({ search: `?${params.toString()}` }, { replace: true });
  };

  /** False (and the Instructions card says why) when an enabled skill has none. */
  const checkInstructions = (enabling: boolean): boolean => {
    if (!enabling || ready) return true;
    setNeedsInstructions(true);
    // The card lives on Overview. Runs, History and SKILL.md hide it, so
    // open Overview and focus once the card mounts.
    if (tab === "overview") {
      instructionsRef.current
        ?.querySelector<HTMLTextAreaElement>("textarea")
        ?.focus();
    } else {
      pendingInstructionFocus.current = true;
      setTab("overview");
    }
    return false;
  };

  const afterSave = useCallback(
    (saved: FirmSkill, opts: { test?: boolean; optimize?: boolean } = {}) => {
      invalidateActiveFirmSkills();
      if (!isNew) {
        // Re-read what was saved: ask the builder to tighten it.
        if (opts.optimize) {
          allowLeave.current = true;
          navigate(automationsPath({ skillId: saved.id, optimize: true }), {
            replace: true
          });
        }
        return;
      }
      allowLeave.current = true;
      onCreated?.(saved, opts);
      if (opts.test) return;
      toast.success(
        saved.status === "active"
          ? "Automation saved and enabled"
          : "Automation saved",
        {
          action: {
            label: "Test on a Case",
            onClick: () =>
              navigate(automationsPath({ skillId: saved.id }), {
                state: { [SKILL_TEST_STATE]: true }
              })
          }
        }
      );
    },
    [isNew, onCreated, navigate]
  );

  const saveNow = async (opts: { test?: boolean } = {}) => {
    if (!checkInstructions(opts.test || content.status === "active")) return;
    // Only new words are worth a pass: not a test, not a change elsewhere.
    const optimizing =
      optimize && !opts.test && draft.dirty.content && ready && canEdit;
    const saved = await draft.save({ quiet: isNew });
    if (saved) afterSave(saved, { ...opts, optimize: optimizing });
  };

  const saveRef = useRef(saveNow);
  saveRef.current = saveNow;
  const canSaveRef = useRef(false);
  canSaveRef.current = dirty && !draft.saving;
  useEffect(() => {
    if (!canEdit) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (!isSaveKey(e)) return;
      e.preventDefault();
      if (canSaveRef.current) void saveRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canEdit]);

  // Discard: put the saved words back; a new skill has none, so it leaves.
  const discardNow = () => {
    draft.discard();
    if (!isNew) return;
    allowLeave.current = true;
    onDiscard?.();
  };

  const toggle = async (enabled: boolean) => {
    if (isNew) {
      setContent((c) => ({ ...c, status: enabled ? "active" : "draft" }));
      if (!enabled) setNeedsInstructions(false);
      return;
    }
    if (!checkInstructions(enabled)) return;
    const next: FirmSkillStatus = enabled ? "active" : "archived";
    setPendingStatus(next);
    try {
      if (enabled && dirty) {
        // Unsaved words: enable them, not the saved ones.
        const saved = await draft.save({ status: "active" });
        if (!saved) return;
        afterSave(saved);
      } else {
        await skills.setStatus(skill.id, next);
        invalidateActiveFirmSkills();
        toast.success(enabled ? "Automation enabled" : "Automation archived");
      }
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Could not update the automation."
      );
    } finally {
      setPendingStatus(null);
    }
  };

  const save = async (edits: SkillEdits) => {
    setSaving(true);
    try {
      await skills.update(skill.id, edits);
      invalidateActiveFirmSkills();
      setEditing(false);
      toast.success("Automation saved");
    } finally {
      setSaving(false);
    }
  };

  const runOn = async ({ ref, inputs, mode, test }: RunSkillRequest) => {
    if (!ref) return;
    try {
      const started = await firmKnowledgeApi.startSkillRun(
        skill.id,
        {
          individual_id: ref.individualId,
          inputs,
          mode,
          ...(test ? { test: true } : {})
        },
        firm.scope
      );
      setRunning(null);
      const params = new URLSearchParams({
        [THREAD_PARAM]: started.thread_id,
        [RUN_PARAM]: started.run.id
      });
      navigate(`${caseWorkspacePath(ref)}?${params.toString()}`, {
        state: { skillRunSeed: started.seed_message }
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not start the run.");
    }
  };

  const metricParts =
    metrics.runs > 0
      ? [
          `${metrics.runs} ${metrics.runs === 1 ? "run" : "runs"}`,
          `${metrics.completed} completed`,
          metrics.maybe_incomplete ? (
            <span className="text-warn" data-testid="skill-metrics-incomplete">
              {metrics.maybe_incomplete} may be incomplete
            </span>
          ) : null,
          metrics.failed ? `${metrics.failed} failed` : null,
          metrics.needs_attention
            ? `${metrics.needs_attention} waiting on someone`
            : null,
          <span key="ratings" className="inline-flex items-center gap-1">
            <ThumbsUp className="size-3" /> {metrics.thumbs_up}
            <ThumbsDown className="ml-1 size-3" /> {metrics.thumbs_down}
          </span>,
          metrics.last_run_at ? (
            <span title={fullDate(metrics.last_run_at)}>
              Last run {shortDate(metrics.last_run_at)}
            </span>
          ) : null,
          automated?.runs ? (
            <span data-testid="skill-metrics-automated">
              {automated.runs} started by automations
              {automated.no_action_rate !== null
                ? ` · ${Math.round(
                    automated.no_action_rate * 100
                  )}% needed nothing`
                : ""}
              {automated.approval_rate !== null
                ? ` · ${Math.round(
                    automated.approval_rate * 100
                  )}% of proposals approved`
                : ""}
            </span>
          ) : null
        ]
      : [];

  const openMarkdown = (edit: boolean) => {
    setMarkdownOpen(true);
    setEditing(edit);
  };

  const otherSkills = skills.skills.filter(
    (s) => s.id !== skill.id && s.status !== "archived"
  );
  // Empty box: the call to action sits over it, with a line saying what it does.
  const emptyBuilderAction =
    canEdit && onAskBuilder ? (
      <>
        <Button
          size="md"
          className={cn(CASEY_BUILD, "button-shimmer button-shimmer-accent")}
          onClick={() => onAskBuilder(content)}
          data-testid="skill-write-with-builder"
        >
          <Wand2 aria-hidden /> Let Casey Build It for You
        </Button>
        <span className="text-sm text-fg-3">
          Describe what you want and Casey writes the steps
        </span>
      </>
    ) : null;
  const instructionsFooter = (
    <>
      <span data-testid="skill-default-mode-chip">{modeLine(draft)}</span>
      <span className="flex-1" />
      {canEdit ? (
        <Label
          htmlFor="skill-optimize"
          className="flex shrink-0 items-center gap-1.5 text-sm font-normal text-fg-2"
          title="After you save, the builder reads the instructions and suggests tighter wording. You approve any change."
        >
          <Switch
            id="skill-optimize"
            size="sm"
            checked={optimize}
            onCheckedChange={(on) => {
              setOptimize(on);
              writeOptimizePref(on);
            }}
            data-testid="skill-optimize"
          />
          Optimize With Builder
        </Label>
      ) : null}
      {firstReference ? (
        <span className="truncate text-fg-4">
          Uses {firstReference.name ?? firstReference.entry_id}
        </span>
      ) : null}
      {canEdit && onAskBuilder && content.instructions.trim() ? (
        <Button
          size="xs"
          className={cn(CASEY_BUILD, "-mr-1")}
          onClick={() => onAskBuilder(content)}
          data-testid="skill-write-with-builder"
        >
          <Wand2 aria-hidden /> Let Casey Build It for You
        </Button>
      ) : null}
    </>
  );

  // Open when something in it is set; a blank automation keeps it folded.
  const advancedDefault =
    !isNew &&
    (draft.triggers.length > 0 ||
      skill.uses_skills.length > 0 ||
      skill.references.length > 0 ||
      draft.authority.default_mode !== "agent" ||
      draft.authority.confirm_before.length > 0 ||
      Boolean(draft.authority.notes.trim()));

  const statusLabel = isNew
    ? active
      ? "Enabled"
      : "Off"
    : SKILL_STATUS_LABEL[status];

  return (
    <div
      className="flex h-full min-h-0 flex-col"
      data-testid="skill-preview"
      data-skill-id={skill.id}
    >
      <UnsavedChangesGuard
        when={dirty}
        leaves={leaves}
        onDiscard={draft.discard}
      />
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-line-2 px-3">
        <div className="flex min-w-0 flex-1 items-center">{leading}</div>
        <SkillSettingsSave
          draft={draft}
          canEdit={canEdit}
          onSave={() => void saveNow()}
          onDiscard={discardNow}
        />
        {canEdit ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={draft.saving || (!isNew && skill.status === "archived")}
            title={
              isNew
                ? "Save it, then run it on a case in Plan First; it changes nothing"
                : "Run it on a case in Plan First; it changes nothing"
            }
            onClick={() =>
              isNew ? void saveNow({ test: true }) : setRunning("test")
            }
            data-testid="skill-test"
          >
            <FlaskConical /> Test
          </Button>
        ) : null}
        {onAskBuilder && canEdit ? (
          <Button
            variant="ghost"
            size="sm"
            title="Ask the Builder to Write or Change This Automation"
            onClick={() => onAskBuilder(content)}
            data-testid="skill-builder-expand"
          >
            <Wand2 className="text-purple" /> Ask the Builder
          </Button>
        ) : null}
        {isNew ? null : (
          <Button
            size="sm"
            variant="secondary"
            disabled={skill.status !== "active"}
            title={skill.status !== "active" ? "Enable it to run" : undefined}
            onClick={() => setRunning("run")}
            data-testid="skill-run"
          >
            <Play /> Run on a Case
          </Button>
        )}
        {isNew && !canEdit ? null : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="More"
                data-testid="skill-menu"
              >
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-48">
              {canEdit ? (
                <DropdownMenuItem
                  onSelect={() => setDescribing(true)}
                  data-testid="skill-edit-description"
                >
                  <TextQuote /> Edit Description
                </DropdownMenuItem>
              ) : null}
              {isNew ? null : (
                <DropdownMenuItem onSelect={() => openMarkdown(false)}>
                  <FileText /> Open SKILL.md
                </DropdownMenuItem>
              )}
              {canEdit && !isNew ? (
                <>
                  <DropdownMenuItem
                    onSelect={() => openMarkdown(true)}
                    data-testid="skill-edit"
                  >
                    <Pencil /> Edit SKILL.md
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() =>
                      navigate(
                        automationsPath({
                          newSkill: true,
                          duplicate: skill.id
                        })
                      )
                    }
                    data-testid="skill-duplicate"
                  >
                    <Copy /> Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() =>
                      void save({
                        visibility:
                          skill.visibility === "hidden" ? "listed" : "hidden"
                      })
                    }
                    data-testid="skill-visibility"
                  >
                    {skill.visibility === "hidden" ? (
                      <>
                        <Eye /> Show In Composer
                      </>
                    ) : (
                      <>
                        <EyeOff /> Hide From Composer
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {skill.status !== "archived" ? (
                    <DropdownMenuItem onSelect={() => void toggle(false)}>
                      <Archive /> Archive
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onSelect={() => void toggle(true)}>
                      <Sparkles /> Enable
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={() => setConfirmDelete(true)}
                    data-testid="skill-delete"
                  >
                    <Trash2 /> Delete
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as Tab)}
        className="min-h-0 flex-1 gap-0 overflow-y-auto"
      >
        <div className="mx-auto flex w-full max-w-180 flex-col gap-4 px-6 pt-6 pb-4">
          {canEdit ? (
            <div className="-mx-1 flex min-w-0 flex-col">
              <input
                ref={titleRef}
                value={content.title}
                onChange={(e) =>
                  setContent((c) => ({ ...c, title: e.target.value }))
                }
                placeholder="Untitled Automation"
                aria-label="Automation name"
                className="w-full rounded-md bg-transparent px-1 text-xl font-normal text-fg transition-colors duration-100 outline-none placeholder:text-fg-4 hover:bg-fill-1 focus-visible:bg-fill-1"
                data-testid="skill-title-input"
              />
            </div>
          ) : (
            <div className="flex min-w-0 flex-col gap-1">
              <h1
                className="text-xl font-normal text-fg"
                data-testid="skill-title"
              >
                {skill.title || skill.slug}
              </h1>
              {skill.description ? (
                <p
                  className="text-base text-fg-2"
                  data-testid="skill-description"
                >
                  {skill.description}
                </p>
              ) : null}
            </div>
          )}
          <div
            className="flex flex-wrap items-center gap-x-3 gap-y-1 text-base text-fg-2"
            data-testid="skill-meta"
          >
            {canEdit ? (
              <>
                <span className="flex items-center gap-2">
                  <Switch
                    id={`skill-enable-${skill.id}`}
                    checked={active}
                    disabled={pendingStatus !== null || draft.saving}
                    onCheckedChange={(v) => void toggle(v)}
                    aria-label={
                      active
                        ? "Turn the automation off"
                        : "Enable the automation"
                    }
                    data-testid="skill-enable"
                  />
                  <label
                    htmlFor={`skill-enable-${skill.id}`}
                    data-testid="skill-enable-label"
                  >
                    {statusLabel}
                  </label>
                  {pendingStatus !== null ? (
                    <Spinner className="size-3.5" />
                  ) : null}
                </span>
                <Separator />
              </>
            ) : null}
            {canEdit ? (
              <VisaPicker
                variant="inline"
                multiple
                value={draft.visas}
                onChange={draft.setVisas}
                disabled={draft.saving}
                placeholder="Any Visa"
                testId="skill-visas"
              />
            ) : (
              <span
                className={visas.length ? undefined : "text-fg-3"}
                data-testid="skill-visas-readonly"
              >
                {visas.length ? visas.join(", ") : "Any Visa"}
              </span>
            )}
            {author || updated ? (
              <>
                <Separator />
                <span
                  title={`Version ${skill.version} · ${fullDate(
                    skill.updated_at ?? skill.created_at
                  )}`}
                  data-testid="skill-updated"
                >
                  {author ? `By ${author}` : null}
                  {author && updated ? " · " : null}
                  {updated ? (
                    <span className="text-fg-3">Updated {updated}</span>
                  ) : null}
                </span>
              </>
            ) : null}
          </div>
          {tab === "markdown" ? null : (
            <TabsList variant="pill">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger
                value="runs"
                disabled={isNew}
                data-testid="skill-tab-runs"
              >
                Runs
                {runs.runs.length ? (
                  <span className="text-fg-4 tabular-nums">
                    {runs.runs.length}
                  </span>
                ) : null}
              </TabsTrigger>
              <TabsTrigger
                value="history"
                disabled={isNew}
                data-testid="skill-tab-history"
              >
                History
              </TabsTrigger>
            </TabsList>
          )}
        </div>

        <TabsContent value="overview" className="flex-none">
          <div className="mx-auto flex w-full max-w-180 flex-col gap-8 px-6 pt-2 pb-8">
            <Section title="Instructions" testId="skill-instructions">
              <ListCard
                ref={instructionsCardRef}
                tone={instructionsError ? "danger" : undefined}
                data-invalid={instructionsError || undefined}
              >
                {canEdit ? (
                  <InstructionsEditor
                    value={content.instructions}
                    onChange={(instructions) => {
                      setContent((c) => ({ ...c, instructions }));
                      if (instructions.trim()) setNeedsInstructions(false);
                    }}
                    onMentionsChange={draft.setMentioned}
                    library={library}
                    skills={otherSkills}
                    footer={instructionsFooter}
                    emptyAction={emptyBuilderAction}
                  />
                ) : (
                  <>
                    <div className="p-3">
                      {skill.instructions ? (
                        <ChatProse>{skill.instructions}</ChatProse>
                      ) : (
                        <p className="text-base text-fg-3">
                          No instructions yet.
                        </p>
                      )}
                    </div>
                    <div className="flex min-w-0 items-center gap-2 px-3 py-2 text-sm text-fg-2">
                      {instructionsFooter}
                    </div>
                  </>
                )}
              </ListCard>
              {instructionsError ? (
                <p
                  className="text-sm text-danger"
                  role="alert"
                  data-testid="skill-instructions-error"
                >
                  Add instructions so agents know what to do.
                </p>
              ) : null}
            </Section>
            <Collapsible
              open={advancedOpen ?? advancedDefault}
              onOpenChange={setAdvancedOpen}
              data-testid="skill-advanced"
            >
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="group flex w-full items-center gap-1.5 text-base text-fg-2 transition-colors hover:text-fg"
                  data-testid="skill-advanced-toggle"
                >
                  <ChevronRight className="size-3.5 transition-transform group-data-[state=open]:rotate-90" />
                  Advanced
                  <span className="text-fg-4">
                    Schedule, Permissions, Related Automations, References
                  </span>
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="flex flex-col gap-8 pt-4">
                <div className="flex flex-col gap-4">
                  <TriggersSection
                    skill={skill}
                    skills={skills}
                    draft={draft}
                    canEdit={canEdit}
                    scope={firm.scope}
                    isNew={isNew}
                  />
                  {isNew ? null : (
                    <UpcomingAutomations skill={skill} scope={firm.scope} />
                  )}
                </div>
                <AuthoritySection draft={draft} canEdit={canEdit} />
                {skill.uses_skills.length ? (
                  <Section
                    title="Follows These Automations"
                    testId="skill-uses"
                  >
                    <ListCard>
                      {skill.uses_skills.map((u) => (
                        <button
                          key={u.skill_id}
                          type="button"
                          className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left transition-colors hover:bg-fill-1"
                          onClick={() =>
                            navigate(automationsPath({ skillId: u.skill_id }))
                          }
                          data-testid="skill-use"
                        >
                          <Sparkles className="size-3.5 shrink-0 text-fg-3" />
                          <span className="min-w-0 flex-1 truncate text-base text-fg">
                            {u.title ?? u.slug ?? u.skill_id}
                          </span>
                          {u.missing ? (
                            <StatusBadge tone="danger">
                              {u.status && u.status !== "active"
                                ? "Not Enabled"
                                : "Gone"}
                            </StatusBadge>
                          ) : null}
                        </button>
                      ))}
                    </ListCard>
                  </Section>
                ) : null}
                <Section title="References" testId="skill-references">
                  {skill.references.length === 0 ? (
                    <p className="text-base text-fg-3">
                      No library documents attached. Attach templates and
                      samples the steps should follow, or type @ in the
                      instructions to point at one.
                    </p>
                  ) : (
                    <ListCard>
                      {skill.references.map((r) => (
                        <button
                          key={r.entry_id}
                          type="button"
                          className="flex w-full items-start gap-2.5 px-4 py-2.5 text-left transition-colors hover:bg-fill-1"
                          onClick={() =>
                            navigate(firmKnowledgePath({ entryId: r.entry_id }))
                          }
                          data-testid="skill-reference"
                        >
                          <FileText className="mt-0.5 size-3.5 shrink-0 text-fg-3" />
                          <span className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate text-base text-fg">
                              {r.name ?? r.entry_id}
                            </span>
                            <span className="text-sm text-fg-3">
                              <span className="capitalize">{r.role}</span>
                              {r.note ? ` · ${r.note}` : ""}
                            </span>
                          </span>
                          {r.missing ? (
                            <StatusBadge tone="danger">Deleted</StatusBadge>
                          ) : null}
                        </button>
                      ))}
                    </ListCard>
                  )}
                  {canEdit && !isNew ? (
                    <SuggestedReferences
                      skill={skill}
                      skills={skills}
                      scope={firm.scope}
                    />
                  ) : null}
                </Section>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </TabsContent>

        <TabsContent value="runs" className="flex-none">
          <div className="mx-auto flex w-full max-w-180 flex-col">
            {metricParts.length ? (
              <MetaLine
                parts={metricParts}
                className="flex-wrap px-6 pt-2"
                data-testid="skill-metrics"
              />
            ) : null}
            <SkillRunsList
              runs={runs.runs}
              loading={runs.loading}
              error={runs.error}
              onRate={runs.rate}
            />
          </div>
        </TabsContent>

        <TabsContent value="history" className="flex flex-none flex-col">
          <div className="mx-auto flex w-full max-w-180 flex-col">
            {isNew ? null : (
              <SkillHistory
                skill={skill}
                scope={firm.scope}
                canEdit={canEdit}
                onReverted={(next) => {
                  skills.replaceSkill(next);
                  invalidateActiveFirmSkills();
                }}
              />
            )}
          </div>
        </TabsContent>

        <TabsContent value="markdown" className="flex-none">
          <div className="mx-auto flex w-full max-w-180 flex-col gap-3 px-6 pt-2 pb-8">
            <div className="flex items-center gap-3">
              <Button
                size="xs"
                variant="ghost"
                className="-ml-2 text-fg-2"
                onClick={() => setTab("overview")}
                data-testid="skill-markdown-back"
              >
                <ArrowLeft /> Back to Overview
              </Button>
              <MetaLine
                data-testid="skill-identity"
                parts={[
                  <>
                    Agent name <span className="font-mono">{skill.slug}</span>
                  </>,
                  `Version ${skill.version}`
                ]}
              />
            </div>
            {editing && canEdit ? (
              <SkillEditor
                skill={skill}
                library={library}
                otherSkills={skills.skills}
                saving={saving}
                onSave={save}
                onCancel={() => setEditing(false)}
              />
            ) : (
              <pre
                className="rounded-xl bg-fill-1 p-4 font-mono text-sm leading-5 whitespace-pre-wrap text-fg-2"
                data-testid="skill-markdown"
              >
                {skill.markdown ?? skill.instructions}
              </pre>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {canEdit && !(markdownOpen && editing) ? (
        <SkillSaveBar
          dirty={dirty}
          saving={draft.saving}
          changed={draft.changed}
          onSave={() => void saveNow()}
          onDiscard={discardNow}
        />
      ) : null}

      <RunSkillDialog
        open={running !== null}
        onOpenChange={(open) => {
          if (!open) setRunning(null);
        }}
        test={running === "test"}
        skill={{
          title: skill.title || skill.slug,
          inputs: skill.inputs,
          defaultMode: skill.authority.default_mode,
          visas
        }}
        onStart={runOn}
      />
      <EditDescriptionDialog
        open={describing}
        onOpenChange={setDescribing}
        value={content.description}
        auto={content.description_auto}
        onDone={(description) =>
          setContent((c) => ({ ...c, description, description_auto: false }))
        }
        onReset={() =>
          setContent((c) => ({
            ...c,
            // A saved written one is the one to go back to; else Save writes it.
            description: isNew
              ? ""
              : skill.description_auto
              ? skill.description
              : c.description,
            description_auto: true
          }))
        }
      />
      <DeleteSkillDialog
        skill={skill}
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        skills={skills}
        onDeleted={() => {
          allowLeave.current = true;
          navigate(automationsPath());
        }}
      />
    </div>
  );
};
